import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Check } from '@phosphor-icons/react';
import StarRating from './StarRating';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PendingReviewsCard — shown on Customer and Captain dashboards.
 * Lists completed bookings with unrated vendors/pros (within the 7-day window)
 * and lets the user rate each one with 1–5 stars.
 *
 * props: testId (string), emptyLabel (string, optional)
 */
const PendingReviewsCard = ({ testId = 'pending-reviews', emptyLabel = 'No pending ratings. Check back after a captain marks your next event complete.' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // `${bookingId}-${targetId}` while submitting

  const fetchPending = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/pending-reviews`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const submitRating = async (bookingId, targetId, stars) => {
    const key = `${bookingId}-${targetId}`;
    setSaving(key);
    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, {
        booking_id: bookingId, target_id: targetId, stars,
      }, { withCredentials: true });
      toast.success('Rating submitted');
      // Optimistic update
      setItems(prev => prev
        .map(b => b.booking_id === bookingId ? {
          ...b,
          targets: b.targets.map(t => t.target_id === targetId
            ? { ...t, already_rated: true, my_stars: stars }
            : t),
        } : b)
        // Drop bookings where every target is now rated
        .filter(b => b.targets.some(t => !t.already_rated))
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return null;
  if (items.length === 0) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid={testId}>
        <CardContent className="py-4">
          <div className="text-sm text-text-secondary">{emptyLabel}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-amber-50/50 border-amber-200" data-testid={testId}>
      <CardContent className="py-5 space-y-4">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-amber-700 mb-1">Rate Your Recent Events</div>
            <p className="text-sm text-text-secondary">Help others choose — rate the vendors and professionals from your completed events. Window closes 7 days after completion.</p>
          </div>
        </div>

        {items.map((b) => (
          <div key={b.booking_id} className="bg-white rounded border border-amber-200 p-4" data-testid={`pending-booking-${b.booking_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium text-sm text-text-primary">{b.package_name || 'Package Booking'}</div>
                <div className="text-xs text-text-secondary">Event: {b.event_date || '—'} · Completed: {b.completed_at ? new Date(b.completed_at).toLocaleDateString() : '—'}</div>
              </div>
            </div>

            <div className="space-y-2">
              {b.targets.map((t) => {
                const savingThis = saving === `${b.booking_id}-${t.target_id}`;
                return (
                  <div key={t.target_id} className="flex items-center justify-between gap-3 py-2 border-t border-[#E8E1D5] first:border-t-0" data-testid={`pending-target-${t.target_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{t.target_name}</div>
                      {t.target_category && <div className="text-[10px] text-text-secondary uppercase tracking-wider">{t.target_category}</div>}
                    </div>
                    {t.already_rated ? (
                      <div className="flex items-center gap-1.5 text-xs text-green-700">
                        <Check size={16} weight="bold" />
                        <StarRating value={t.my_stars} readonly size={16} testIdPrefix={`rated-${t.target_id}`} />
                      </div>
                    ) : (
                      <StarRating
                        value={0}
                        onChange={(n) => !savingThis && submitRating(b.booking_id, t.target_id, n)}
                        size={22}
                        testIdPrefix={`rate-${t.target_id}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PendingReviewsCard;
